import React, { useState } from 'react';
import { FileText, Presentation, FileImage, Download, Copy, Eye, EyeOff, CheckCircle, Users, Target } from 'lucide-react';

export const ApplicationTemplates: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'proposals' | 'reports' | 'presentations' | 'social' | 'worksheets'>('proposals');
  const [showPreview, setShowPreview] = useState(false);

  const TemplateExample: React.FC<{
    title: string;
    description: string;
    category: string;
    useCase: string;
    features: string[];
    preview: React.ReactNode;
    downloadUrl: string;
  }> = ({ title, description, category, useCase, features, preview, downloadUrl: _downloadUrl }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
          <p className="text-sm text-charcoal-600">{description}</p>
          <p className="text-xs text-charcoal-500 mt-1">Category: {category}</p>
        </div>
        <button 
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showPreview ? 'Hide' : 'Show'} Preview
        </button>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Use Case:</p>
        <p className="text-sm text-charcoal-700">{useCase}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Features:</p>
        <ul className="text-sm text-charcoal-700 space-y-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              <CheckCircle className="h-3 w-3 text-sage-600" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      
      {showPreview && (
        <div className="bg-[var(--surface)] p-4 h-48 flex items-center justify-center relative overflow-hidden mb-4">
          {preview}
        </div>
      )}
      
      <div className="flex gap-2">
        <button className="flex items-center gap-1 px-4 py-2 bg-consulting-slate-blue-600 text-white text-sm font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
          <Download className="h-4 w-4" />
          Download
        </button>
        <button className="flex items-center gap-1 px-4 py-2 bg-[var(--hover-tint)] text-charcoal-700 text-sm font-semibold hover:bg-[var(--hover-tint)] transition-colors">
          <Copy className="h-4 w-4" />
          Copy
        </button>
      </div>
    </div>
  );

  const TemplateCategory: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    templates: React.ReactNode;
  }> = ({ title, description, icon, templates }) => (
    <div className="space-y-8">
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          {icon}
          <h2 className="text-2xl font-bold text-charcoal-900">{title}</h2>
        </div>
        <p className="text-charcoal-700 mb-6">{description}</p>
        {templates}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <FileText className="h-10 w-10 text-[var(--mustard)]" />
          <h1 className="text-4xl font-bold text-charcoal-900">Application Templates</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Pre-built templates for proposals, white papers, case studies, one-pagers, slide decks, social media, and worksheets 
          that streamline production and ensure consistent brand application across all Peak Priorities materials.
        </p>
      </div>

      {/* System Overview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Target className="h-6 w-6 text-[var(--mustard)]" />
          <h2 className="text-2xl font-bold text-charcoal-900">Template System Overview</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Our template system provides ready-to-use layouts that maintain brand consistency while accelerating content creation. 
          Every template follows Peak Priorities design principles and includes proper spacing, typography, and visual hierarchy.
        </p>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Consistent</h3>
            <p className="text-sm text-[var(--mustard)]">All templates follow the same design principles and brand guidelines</p>
          </div>
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Efficient</h3>
            <p className="text-sm text-consulting-slate-blue-800">Ready-to-use layouts that accelerate content creation</p>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4">
            <h3 className="font-bold text-sage-900 mb-2">Flexible</h3>
            <p className="text-sm text-sage-800">Customizable templates that adapt to different content needs</p>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Professional</h3>
            <p className="text-sm text-[var(--mustard)]">High-quality templates that reinforce brand credibility</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 flex-wrap">
        <button
          onClick={() => setActiveTab('proposals')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'proposals'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <FileText className="h-5 w-5" />
          Proposals
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'reports'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <FileText className="h-5 w-5" />
          Reports
        </button>
        <button
          onClick={() => setActiveTab('presentations')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'presentations'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Presentation className="h-5 w-5" />
          Presentations
        </button>
        <button
          onClick={() => setActiveTab('social')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'social'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <FileImage className="h-5 w-5" />
          Social Media
        </button>
        <button
          onClick={() => setActiveTab('worksheets')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'worksheets'
              ? 'bg-[var(--mustard)] text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Users className="h-5 w-5" />
          Worksheets
        </button>
      </div>

      {/* Proposals */}
      {activeTab === 'proposals' && (
        <TemplateCategory
          title="Proposal Templates"
          description="Professional proposal templates for client engagements, project bids, and business development."
          icon={<FileText className="h-6 w-6 text-[var(--mustard)]" />}
          templates={
            <div className="grid md:grid-cols-2 gap-6">
              <TemplateExample
                title="Executive Proposal"
                description="High-level proposal for C-suite presentations"
                category="Executive"
                useCase="C-suite presentations, board proposals, strategic initiatives"
                features={[
                  "Executive summary section",
                  "Strategic overview",
                  "ROI calculations",
                  "Implementation timeline",
                  "Risk assessment"
                ]}
                downloadUrl="/templates/executive-proposal.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Technical Proposal"
                description="Detailed technical proposal for implementation projects"
                category="Technical"
                useCase="Technical implementations, system integrations, detailed project plans"
                features={[
                  "Technical specifications",
                  "Implementation methodology",
                  "Resource requirements",
                  "Testing procedures",
                  "Support structure"
                ]}
                downloadUrl="/templates/technical-proposal.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="One-Page Proposal"
                description="Concise single-page proposal for quick decisions"
                category="Quick Decision"
                useCase="Quick approvals, simple projects, follow-up proposals"
                features={[
                  "Single-page format",
                  "Key benefits highlighted",
                  "Clear call-to-action",
                  "Contact information",
                  "Next steps outlined"
                ]}
                downloadUrl="/templates/one-page-proposal.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Partnership Proposal"
                description="Collaboration proposal for strategic partnerships"
                category="Partnership"
                useCase="Strategic partnerships, joint ventures, collaboration agreements"
                features={[
                  "Partnership benefits",
                  "Collaboration framework",
                  "Resource sharing",
                  "Success metrics",
                  "Agreement terms"
                ]}
                downloadUrl="/templates/partnership-proposal.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-coral-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />
            </div>
          }
        />
      )}

      {/* Reports */}
      {activeTab === 'reports' && (
        <TemplateCategory
          title="Report Templates"
          description="Comprehensive report templates for assessments, analyses, and documentation."
          icon={<FileText className="h-6 w-6 text-[var(--mustard)]" />}
          templates={
            <div className="grid md:grid-cols-2 gap-6">
              <TemplateExample
                title="Assessment Report"
                description="Detailed assessment report with findings and recommendations"
                category="Assessment"
                useCase="Client assessments, capability evaluations, performance reviews"
                features={[
                  "Executive summary",
                  "Assessment methodology",
                  "Key findings",
                  "Recommendations",
                  "Implementation roadmap"
                ]}
                downloadUrl="/templates/assessment-report.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="White Paper"
                description="Thought leadership white paper template"
                category="Thought Leadership"
                useCase="Industry insights, best practices, research findings"
                features={[
                  "Abstract section",
                  "Literature review",
                  "Methodology",
                  "Findings",
                  "Conclusions"
                ]}
                downloadUrl="/templates/white-paper.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Case Study"
                description="Client success story case study template"
                category="Case Study"
                useCase="Client success stories, project outcomes, testimonials"
                features={[
                  "Challenge description",
                  "Solution approach",
                  "Implementation process",
                  "Results achieved",
                  "Client testimonial"
                ]}
                downloadUrl="/templates/case-study.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Monthly Report"
                description="Regular monthly progress and status report"
                category="Status Report"
                useCase="Monthly updates, progress tracking, status communications"
                features={[
                  "Progress summary",
                  "Key metrics",
                  "Challenges addressed",
                  "Next month priorities",
                  "Resource needs"
                ]}
                downloadUrl="/templates/monthly-report.docx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-coral-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />
            </div>
          }
        />
      )}

      {/* Presentations */}
      {activeTab === 'presentations' && (
        <TemplateCategory
          title="Presentation Templates"
          description="Professional slide deck templates for various presentation needs."
          icon={<Presentation className="h-6 w-6 text-[var(--mustard)]" />}
          templates={
            <div className="grid md:grid-cols-2 gap-6">
              <TemplateExample
                title="Executive Presentation"
                description="C-suite focused presentation template"
                category="Executive"
                useCase="Board presentations, executive briefings, strategic updates"
                features={[
                  "Executive summary slides",
                  "Strategic overview",
                  "Financial highlights",
                  "Risk assessment",
                  "Next steps"
                ]}
                downloadUrl="/templates/executive-presentation.pptx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Workshop Presentation"
                description="Interactive workshop presentation template"
                category="Workshop"
                useCase="Training sessions, workshops, interactive presentations"
                features={[
                  "Interactive elements",
                  "Exercise slides",
                  "Discussion prompts",
                  "Breakout activities",
                  "Action planning"
                ]}
                downloadUrl="/templates/workshop-presentation.pptx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Sales Presentation"
                description="Client-focused sales presentation template"
                category="Sales"
                useCase="Client presentations, sales pitches, proposal presentations"
                features={[
                  "Problem identification",
                  "Solution presentation",
                  "Value proposition",
                  "Case studies",
                  "Call to action"
                ]}
                downloadUrl="/templates/sales-presentation.pptx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-coral-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Training Presentation"
                description="Educational training presentation template"
                category="Training"
                useCase="Employee training, skill development, educational content"
                features={[
                  "Learning objectives",
                  "Content modules",
                  "Knowledge checks",
                  "Practice exercises",
                  "Assessment"
                ]}
                downloadUrl="/templates/training-presentation.pptx"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />
            </div>
          }
        />
      )}

      {/* Social Media */}
      {activeTab === 'social' && (
        <TemplateCategory
          title="Social Media Templates"
          description="Branded social media templates for consistent online presence."
          icon={<FileImage className="h-6 w-6 text-[var(--mustard)]" />}
          templates={
            <div className="grid md:grid-cols-2 gap-6">
              <TemplateExample
                title="LinkedIn Post"
                description="Professional LinkedIn post template"
                category="LinkedIn"
                useCase="Professional updates, thought leadership, company news"
                features={[
                  "Branded header",
                  "Content area",
                  "Call-to-action",
                  "Hashtag section",
                  "Company branding"
                ]}
                downloadUrl="/templates/linkedin-post.psd"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Twitter Post"
                description="Twitter post template with character limits"
                category="Twitter"
                useCase="Quick updates, announcements, engagement"
                features={[
                  "Character-optimized layout",
                  "Visual elements",
                  "Hashtag integration",
                  "Brand consistency",
                  "Engagement prompts"
                ]}
                downloadUrl="/templates/twitter-post.psd"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Instagram Post"
                description="Visual Instagram post template"
                category="Instagram"
                useCase="Visual content, behind-the-scenes, brand stories"
                features={[
                  "Square format",
                  "Visual focus",
                  "Brand elements",
                  "Caption area",
                  "Story highlights"
                ]}
                downloadUrl="/templates/instagram-post.psd"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-coral-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Facebook Post"
                description="Facebook post template for broader reach"
                category="Facebook"
                useCase="Community updates, event promotion, broader reach"
                features={[
                  "Wide format",
                  "Engagement elements",
                  "Community focus",
                  "Event integration",
                  "Share optimization"
                ]}
                downloadUrl="/templates/facebook-post.psd"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />
            </div>
          }
        />
      )}

      {/* Worksheets */}
      {activeTab === 'worksheets' && (
        <TemplateCategory
          title="Worksheet Templates"
          description="Interactive worksheet templates for assessments, planning, and analysis."
          icon={<Users className="h-6 w-6 text-[var(--mustard)]" />}
          templates={
            <div className="grid md:grid-cols-2 gap-6">
              <TemplateExample
                title="Priority Assessment"
                description="Interactive priority assessment worksheet"
                category="Assessment"
                useCase="Priority evaluation, decision-making, resource allocation"
                features={[
                  "Priority matrix",
                  "Scoring system",
                  "Decision framework",
                  "Action planning",
                  "Follow-up tracking"
                ]}
                downloadUrl="/templates/priority-assessment.pdf"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Project Planning"
                description="Comprehensive project planning worksheet"
                category="Planning"
                useCase="Project initiation, planning, resource allocation"
                features={[
                  "Project scope",
                  "Timeline planning",
                  "Resource allocation",
                  "Risk assessment",
                  "Success metrics"
                ]}
                downloadUrl="/templates/project-planning.pdf"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-sage-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Stakeholder Analysis"
                description="Stakeholder mapping and analysis worksheet"
                category="Analysis"
                useCase="Stakeholder identification, influence mapping, engagement planning"
                features={[
                  "Stakeholder mapping",
                  "Influence analysis",
                  "Engagement strategy",
                  "Communication plan",
                  "Relationship tracking"
                ]}
                downloadUrl="/templates/stakeholder-analysis.pdf"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-coral-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />

              <TemplateExample
                title="Decision Framework"
                description="Structured decision-making worksheet"
                category="Decision Making"
                useCase="Complex decisions, trade-off analysis, option evaluation"
                features={[
                  "Decision criteria",
                  "Option evaluation",
                  "Trade-off analysis",
                  "Risk assessment",
                  "Decision documentation"
                ]}
                downloadUrl="/templates/decision-framework.pdf"
                preview={
                  <div className="w-full h-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                    <div className="h-4 bg-[var(--mustard)] rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-2"></div>
                    <div className="h-4 bg-consulting-slate-blue-600 rounded mb-2"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                    <div className="h-3 bg-[var(--rule-bright)] rounded mb-1"></div>
                  </div>
                }
              />
            </div>
          }
        />
      )}

      {/* Implementation Guidelines */}
      <div className="bg-gradient-to-r from-amber-50 to-consulting-slate-blue-50 border-2 border-[var(--mustard)] p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-charcoal-700 max-w-3xl mx-auto mb-6">
          Use these templates consistently across all Peak Priorities materials. 
          They provide a foundation for professional, brand-consistent content creation while maintaining flexibility for customization.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--mustard)] text-white font-semibold hover:bg-[var(--mustard)] transition-colors">
            <Download className="h-5 w-5" />
            Download All Templates
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--bg-raised)] text-charcoal-900 border-2 border-[var(--mustard)] font-semibold hover:bg-[var(--mustard-dim)] transition-colors">
            <Copy className="h-5 w-5" />
            Request Custom Template
          </button>
        </div>
      </div>
    </div>
  );
};




